import type { BucketKey, CallRow, Filters, StageKey, StageSplit, WorkRow } from './types';
import { ratio } from './format';
import { emptyStages } from './adapt';

/**
 * A customer who appears under two agents or two buckets is one customer. The live report
 * counts distinct people at whatever grain is selected; the sample just trims the row sum.
 */
const DEDUPE = 1;

export interface Totals {
  /** Work items across all buckets (ABC: one per cart event). */
  workItems: number;
  uniqueCustomers: number;
  uniqueOrders: number;
  /** Volume in each bucket's own unit -- use only within one bucket. */
  received: number;
  worked: number; unworked: number; workRate: number | null;
  workedManual: number; workedTata: number;
  closedByAgent: number; closedBySystem: number; open: number;
  orders: number; validOrders: number; cancelledOrders: number; units: number;
  validRevenue: number; cancelledValue: number; aov: number | null;
  tagOrders: number; couponOrders: number;
  /** Agent Dashboard columns: everything credited, split into five stages that add up to it. */
  booked: number; stages: Record<StageKey, StageSplit>;
  /** Of orders that finished their journey (delivered or returned). */
  deliveryPct: number | null; inTransitPct: number | null; rtoPct: number | null;
  /** Orders / unique customers worked. Null when no customers. */
  convPct: number | null; bookedAov: number | null;
}

/**
 * Delivery %, In Transit % and RTO % share one base: orders that have shipped (delivered + in transit + returned),
 * so the three add up to 100%. Cancelled and awaiting-pickup orders are not in the base.
 */
export function shippedShares(st: Record<StageKey, StageSplit>) {
  const shipped = st.delivered.orders + st.inTransit.orders + st.returned.orders;
  return {
    deliveryPct: ratio(st.delivered.orders, shipped), inTransitPct: ratio(st.inTransit.orders, shipped), rtoPct: ratio(st.returned.orders, shipped),
  };
}

export function totals(rows: WorkRow[]): Totals {
  const t = rows.reduce((a, r) => ({
    workItems: a.workItems + r.workItems,
    customers: a.customers + r.uniqueCustomers, uniqOrders: a.uniqOrders + (r.uniqueOrders ?? 0),
    received: a.received + r.received, worked: a.worked + r.worked,
    workedManual: a.workedManual + r.workedManual, workedTata: a.workedTata + r.workedTata,
    closedByAgent: a.closedByAgent + r.closedByAgent, closedBySystem: a.closedBySystem + r.closedBySystem,
    orders: a.orders + r.orders, validOrders: a.validOrders + r.validOrders, cancelledOrders: a.cancelledOrders + r.cancelledOrders,
    units: a.units + r.units, validRevenue: a.validRevenue + r.validRevenue, cancelledValue: a.cancelledValue + r.cancelledValue,
    tagOrders: a.tagOrders + r.tagOrders, couponOrders: a.couponOrders + r.couponOrders,
  }), {
    workItems: 0, customers: 0, uniqOrders: 0, received: 0, worked: 0, workedManual: 0, workedTata: 0,
    closedByAgent: 0, closedBySystem: 0, orders: 0, validOrders: 0, cancelledOrders: 0, units: 0,
    validRevenue: 0, cancelledValue: 0, tagOrders: 0, couponOrders: 0,
  });
  const dedupe = DEDUPE;
  const stages = rows.reduce((a, r) => {
    (Object.keys(a) as StageKey[]).forEach(k => { a[k].orders += r.stages[k].orders; a[k].value += r.stages[k].value; });
    return a;
  }, emptyStages());
  const booked = rows.reduce((a, r) => a + r.booked, 0);
  return {
    booked, stages,
    ...shippedShares(stages),
    convPct: ratio(t.orders, t.customers), bookedAov: t.orders > 0 ? Math.round(booked / t.orders) : null,
    workItems: t.workItems,
    uniqueCustomers: Math.round(t.customers * dedupe), uniqueOrders: t.uniqOrders,
    received: t.received, worked: t.worked, unworked: t.received - t.worked, workRate: ratio(t.worked, t.received),
    workedManual: t.workedManual, workedTata: t.workedTata,
    closedByAgent: t.closedByAgent, closedBySystem: t.closedBySystem,
    open: t.received - t.closedByAgent - t.closedBySystem,
    orders: t.orders, validOrders: t.validOrders, cancelledOrders: t.cancelledOrders, units: t.units,
    validRevenue: t.validRevenue, cancelledValue: t.cancelledValue,
    aov: t.validOrders > 0 ? Math.round(t.validRevenue / t.validOrders) : null,
    tagOrders: t.tagOrders, couponOrders: t.couponOrders,
  };
}


/** Only Agent and Bucket move the sample numbers; the rest show where they will sit. */
export function applyFilters(rows: WorkRow[], f: Filters): WorkRow[] {
  return rows.filter(r =>
    (f.agent === 'all' || r.agent === f.agent) && (f.buckets.length === 0 || f.buckets.includes(r.bucket as BucketKey)));
}

export interface CallTotals {
  dials: number; connected: number; notConnected: number; connectRate: number | null; notConnectedRate: number | null;
  noAnswer: number; cancelledRinging: number; droppedOther: number; hungUpEarly: number;
  talkSeconds: number; talkHours: number; avgTalk: number | null; avgRing: number | null;
  shortCalls: number; shortRate: number | null; customersCalled: number; customersConnected: number; neverConnected: number;
  neverRate: number | null; attemptsPerCustomer: number | null; activeDays: number;
}

export function callTotals(rows: CallRow[]): CallTotals {
  const t = rows.reduce((a, r) => ({
    dials: a.dials + r.dials, connected: a.connected + r.connected, noAnswer: a.noAnswer + r.noAnswer,
    cancelledRinging: a.cancelledRinging + r.cancelledRinging, droppedOther: a.droppedOther + r.droppedOther,
    hungUpEarly: a.hungUpEarly + r.hungUpEarly, talk: a.talk + r.talkSeconds, short: a.short + r.shortCalls,
    ring: a.ring + r.ringSeconds, called: a.called + r.customersCalled, reached: a.reached + r.customersConnected,
    days: Math.max(a.days, r.activeDays),
  }), { dials: 0, connected: 0, noAnswer: 0, cancelledRinging: 0, droppedOther: 0, hungUpEarly: 0, talk: 0, short: 0, ring: 0, called: 0, reached: 0, days: 0 });
  // As with unique customers elsewhere, a person called under two buckets is one person.
  const dedupe = 1;
  const called = Math.round(t.called * dedupe), reached = Math.min(called, Math.round(t.reached * dedupe));
  const notConnected = t.dials - t.connected;
  return {
    dials: t.dials, connected: t.connected, notConnected,
    connectRate: ratio(t.connected, t.dials), notConnectedRate: ratio(notConnected, t.dials),
    noAnswer: t.noAnswer, cancelledRinging: t.cancelledRinging, droppedOther: t.droppedOther, hungUpEarly: t.hungUpEarly,
    talkSeconds: t.talk, talkHours: Math.round((t.talk / 3600) * 10) / 10,
    avgTalk: t.connected ? Math.round(t.talk / t.connected) : null, avgRing: t.dials ? Math.round(t.ring / t.dials) : null,
    shortCalls: t.short, shortRate: ratio(t.short, t.connected),
    customersCalled: called, customersConnected: reached, neverConnected: called - reached, neverRate: ratio(called - reached, called),
    attemptsPerCustomer: called ? Math.round((t.dials / called) * 10) / 10 : null, activeDays: t.days,
  };
}

export function filterCalls(rows: CallRow[], f: Filters): CallRow[] {
  return rows.filter(r => (f.agent === 'all' || r.agent === f.agent) && (f.buckets.length === 0 || f.buckets.includes(r.bucket as BucketKey)));
}

/** 95 -> "1:35". */
export const mmss = (sec: number | null) => (sec === null ? '—' : `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`);

/**
 * Totals for the orders credited by Agent Tag, or by coupon (everything else). Stages are exact: the server counts the tag part
 * and the coupon part is the total minus it. Conv% is left blank: customers are worked per bucket, not per attribution source.
 */
export function attributionTotals(rows: WorkRow[], source: 'tag' | 'coupon'): Totals {
  const all = totals(rows);
  const tag = rows.reduce((a, r) => {
    (Object.keys(a) as StageKey[]).forEach(k => { a[k].orders += r.tag[k].orders; a[k].value += r.tag[k].value; });
    return a;
  }, emptyStages());
  const stages = emptyStages();
  (Object.keys(stages) as StageKey[]).forEach(k => {
    stages[k] = source === 'tag' ? tag[k]
      : { orders: all.stages[k].orders - tag[k].orders, value: all.stages[k].value - tag[k].value };
  });
  const sum = (f: (s: StageSplit) => number) => (Object.values(stages) as StageSplit[]).reduce((a, x) => a + f(x), 0);
  const orders = sum(x => x.orders), booked = sum(x => x.value);
  return {
    ...all, orders, booked, stages, convPct: null,
    ...shippedShares(stages),
    bookedAov: orders > 0 ? Math.round(booked / orders) : null,
  };
}

/** Commercial figures for a Category / Product / Agent row. Conv% is blank: customers are worked per bucket, not per product. */
export function catalogTotals(m: { orders: number; booked: number; stages: Record<StageKey, StageSplit> }) {
  return {
    orders: m.orders, booked: m.booked, stages: m.stages, convPct: null as number | null,
    ...shippedShares(m.stages),
    bookedAov: m.orders > 0 ? Math.round(m.booked / m.orders) : null,
  };
}
